import * as React from "react";
import {Box, styled, Table, TableBody, TableCell, TableHead, TableRow} from "@mui/material";
import {NewDomainAllocation} from "../dynamicDataSource";
import {FormattedMessage, MessageDescriptor} from "react-intl";
import {NumberField} from "@dvrt/graphql-widgets";
import {ErrorWithContext} from "@/utils/ErrorHelper";
import {isUnlimited} from "@/tools";

/**
 * Get the max value for the number field
 *
 * @param allocation The allocation to get the max value for
 * @returns The max value for the number field or undefined if there is no max value
 */
function getMax(allocation: NewDomainAllocation): number | undefined {
    if (allocation.systemLimit == null) return undefined; // if nullish (null / undefined)

    let freeAllocation = 0;

    // currently only ccAattendant has a free allocation
    if (allocation.name === "ccAattendant") {
        freeAllocation = 1;
    }

    // even though the real value in the database includes the free allocation as well,
    // we do not want to count that free one in the form field, because that is displayed
    // separately
    const adjustedDomainAllocated = Math.max(allocation.domainAllocated - freeAllocation, 0);

    return Math.max(
        allocation.systemLimit - (allocation.totalAllocated - adjustedDomainAllocated),
        allocation.domainUsed,
        adjustedDomainAllocated,
    );
}

export type AllocationTableProps = {
    // should returns message descriptor for an allocation name
    labelForAllocation: (allocationName: string) => MessageDescriptor;

    // to be displayed in the first column
    label: MessageDescriptor;

    // The allocations of the form
    allocations: NewDomainAllocation[];

    // Errors
    errors: Record<string, ErrorWithContext[]>;

    // Function to update the allocation when the user changes the value
    update: (name: string, value: number) => void;

    // Whether the user has the permission to edit the domain allocations
    hasEditPermission?: boolean;

    // Whether the user can see the system limit and allocation
    canSeeSystemAllocation?: boolean;

    // Disables the input fields
    disabled?: boolean;
};

const AllocationTable = ({
    label,
    labelForAllocation,
    allocations,
    errors = {},
    update,
    hasEditPermission,
    canSeeSystemAllocation,
    disabled,
}: AllocationTableProps) => {
    return (
        <Table key={"table-" + label.id} style={{width: "fit-content"}}>
            <TableHead>
                <TableRow>
                    <TableCell style={HeaderCellStyle}>
                        <FormattedMessage {...label} />
                    </TableCell>
                    {canSeeSystemAllocation && (
                        <>
                            <TableCell style={HeaderCellStyle}>
                                <FormattedMessage id="domain_settings.system_limit" defaultMessage="System limit" />
                            </TableCell>
                            <TableCell style={HeaderCellStyle}>
                                <FormattedMessage
                                    id="domain_settings.total_allocated"
                                    defaultMessage="Total allocated"
                                />
                            </TableCell>
                        </>
                    )}
                    <TableCell style={HeaderCellStyle}>
                        <FormattedMessage id="domain_settings.domain_allocated" defaultMessage="Domain allocated" />
                    </TableCell>
                    <TableCell style={HeaderCellStyle}>
                        <FormattedMessage id="domain_settings.domain_used" defaultMessage="Domain used" />
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {allocations.map((allocation) => (
                    <AllocationRow
                        key={allocation.name}
                        allocation={allocation}
                        update={(value) => update(allocation.name, value)}
                        errors={errors}
                        labelForAllocation={labelForAllocation}
                        canSeeSystemAllocation={canSeeSystemAllocation}
                        hasEditPermission={hasEditPermission}
                        disabled={disabled}
                    />
                ))}
            </TableBody>
        </Table>
    );
};

type AllocationRowProps = {
    allocation: NewDomainAllocation;
    update: (value: number) => void;
    errors: Record<string, ErrorWithContext[]>;
    labelForAllocation: (allocationName: string) => MessageDescriptor;
    canSeeSystemAllocation?: boolean;
    hasEditPermission?: boolean;
    disabled?: boolean;
};

export const AllocationRow = ({
    allocation,
    update,
    errors,
    labelForAllocation,
    canSeeSystemAllocation,
    hasEditPermission,
    disabled,
}: AllocationRowProps) => {
    const allocationFormValue = allocation.active ? allocation.newAllocation + allocation.included : 0;

    return (
        <TableRow>
            <TableCell style={HeaderCellStyle}>
                <FormattedMessage {...labelForAllocation(allocation.name)} />
            </TableCell>
            {canSeeSystemAllocation && (
                <>
                    <TableCell>
                        {isUnlimited(allocation.systemLimit) ? (
                            <FormattedMessage id="unlimited" defaultMessage="Unlimited" />
                        ) : (
                            allocation.systemLimit
                        )}
                    </TableCell>
                    <TableCell
                        style={
                            allocation.systemLimit != null && allocation.totalAllocated > allocation.systemLimit
                                ? WarningStyle
                                : null
                        }
                    >
                        {allocation.totalAllocated}
                    </TableCell>
                </>
            )}
            <TableCell>
                <AllocationField
                    value={allocation.newAllocation}
                    update={update}
                    errors={errors[allocation.name]}
                    min={Math.max(allocation.domainUsed, 0)}
                    max={getMax(allocation)}
                    hasPermission={hasEditPermission}
                    disabled={disabled}
                    included={allocation.included}
                />
            </TableCell>
            <TableCell style={allocation.domainUsed > allocationFormValue ? WarningStyle : null}>
                {allocation.domainUsed}
            </TableCell>
        </TableRow>
    );
};

type AllocationFieldProps = {
    update: (value: number) => void;
    value: number;
    errors: ErrorWithContext[];
    min: number;
    max: number;
    hasPermission: boolean;
    disabled?: boolean;
    included?: number;
};

const AllocationField = ({
    update,
    value,
    errors,
    min,
    max,
    hasPermission,
    disabled,
    included = 0,
}: AllocationFieldProps) => {
    if (!hasPermission) {
        return <span>{value}</span>;
    }

    return (
        <Box display="flex">
            <NumberField value={value} update={update} errors={errors} min={min} max={max} disabled={disabled} />
            {!disabled && included > 0 && <IncludedSpan>+{included}</IncludedSpan>}
        </Box>
    );
};

const IncludedSpan = styled("span")(({theme}) => ({
    alignSelf: "center",
    marginLeft: theme.spacing(1),
    color: theme.palette.text.disabled,
    pointerEvents: "none",
}));

const WarningStyle = {
    fontWeight: "bold",
    color: "red",
};

const HeaderCellStyle = {
    fontWeight: "bold",
} as React.CSSProperties;

export default AllocationTable;
